import type { CustomLayerInterface, Map as GeographicMap } from "maplibre-gl";
import type { MapFeature, Position } from "./municipality-map-layout";

export const TERRAIN_CAPTURE_ID = "rbim-terrain-flat-capture";
export const TERRAIN_MASK_ID = "rbim-terrain-municipality-mask";

/** Dissolve shared edges, retaining original coordinates (no buffer or snapping).
 * Normalize exterior/hole winding first, then cancel opposing edges and trace
 * closed coverage rings. Nonzero filling also handles collinear subdivided
 * shared edges and overlapping exteriors without per-barangay antialias seams.
 */
export function municipalityMaskRings(features: readonly MapFeature[]): Position[][] {
  const edges = new Map<string, { start: Position; end: Position }[]>();
  const key = (point: Position) => `${point[0]},${point[1]}`;
  for (const feature of features) {
    const polygons = feature.geometry.type === "Polygon" ? [feature.geometry.coordinates] : feature.geometry.coordinates;
    for (const polygon of polygons) polygon.forEach((original, ringIndex) => {
      const ring = original.slice();
      if (ring.length < 4) return;
      const area = ring.slice(1).reduce((sum, point, i) => sum + ring[i][0] * point[1] - point[0] * ring[i][1], 0);
      if ((area > 0) !== (ringIndex === 0)) ring.reverse();
      for (let i = 1; i < ring.length; i++) {
        const start = ring[i - 1], end = ring[i];
        if (key(start) === key(end)) continue;
        const forward = `${key(start)}>${key(end)}`, reverse = `${key(end)}>${key(start)}`;
        const opposite = edges.get(reverse);
        if (opposite?.length) {
          opposite.pop();
          if (!opposite.length) edges.delete(reverse);
        } else {
          const list = edges.get(forward) ?? [];
          list.push({ start, end });
          edges.set(forward, list);
        }
      }
    });
  }
  const outgoing = new Map<string, Position[]>();
  for (const list of edges.values()) for (const { start, end } of list) {
    const next = outgoing.get(key(start)) ?? [];
    next.push(end);
    outgoing.set(key(start), next);
  }
  const rings: Position[][] = [];
  while (outgoing.size) {
    const startKey = outgoing.keys().next().value!;
    const start = startKey.split(",").map(Number) as Position;
    const ring = [start];
    let current = startKey;
    do {
      const next = outgoing.get(current);
      if (!next?.length) throw new Error("Municipality terrain mask must have closed geographic rings.");
      const end = next.pop()!;
      if (!next.length) outgoing.delete(current);
      ring.push(end);
      current = key(end);
    } while (current !== startKey);
    rings.push(ring);
  }
  return rings;
}

/** Public MapLibre custom layers, in one context/camera. Capture the actual flat
 * Liberty framebuffer immediately before hillshade, then restore only its outside
 * pixels immediately afterwards. No replacement colors, second map or CSS clip.
 */
export function municipalityTerrainMaskLayers(map: GeographicMap, getFeatures: () => readonly MapFeature[]): CustomLayerInterface[] {
  const maskCanvas = document.createElement("canvas");
  const maskContext = maskCanvas.getContext("2d");
  if (!maskContext) throw new Error("Municipality terrain mask is unavailable.");
  let flat: WebGLTexture | null = null, mask: WebGLTexture | null = null;
  let program: WebGLProgram | null = null, vao: WebGLVertexArrayObject | null = null;
  let width = 0, height = 0, cameraKey = "";
  let previousFeatures: readonly MapFeature[] | undefined;
  let rings: Position[][] = [];

  function dispose(gl: WebGL2RenderingContext) {
    gl.deleteTexture(flat); gl.deleteTexture(mask); gl.deleteProgram(program); gl.deleteVertexArray(vao);
    flat = mask = program = vao = null;
    width = height = 0; cameraKey = "";
    maskCanvas.width = maskCanvas.height = 0;
  }
  function texture(gl: WebGL2RenderingContext) {
    const result = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, result);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return result;
  }
  const capture: CustomLayerInterface = {
    id: TERRAIN_CAPTURE_ID, type: "custom", renderingMode: "2d",
    onAdd(_map, gl) {
      const shaders: WebGLShader[] = [];
      try {
        program = gl.createProgram();
        for (const [type, source] of [
          [gl.VERTEX_SHADER, `#version 300 es
            out vec2 uv;
            void main() {
              vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
              uv = p; gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
            }`],
          [gl.FRAGMENT_SHADER, `#version 300 es
            precision highp float;
            uniform sampler2D flatBase;
            uniform sampler2D coverage;
            in vec2 uv;
            out vec4 color;
            void main() {
              float outside = 1.0 - texture(coverage, vec2(uv.x, 1.0 - uv.y)).a;
              color = texture(flatBase, uv) * outside;
            }`],
        ] as const) {
          const shader = gl.createShader(type)!;
          shaders.push(shader);
          gl.shaderSource(shader, source); gl.compileShader(shader);
          if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? "Terrain mask shader failed");
          gl.attachShader(program!, shader);
        }
        gl.linkProgram(program!);
        if (!gl.getProgramParameter(program!, gl.LINK_STATUS)) throw new Error("Terrain mask shader link failed");
        flat = texture(gl); mask = texture(gl); vao = gl.createVertexArray();
      } catch (error) {
        dispose(gl);
        throw error;
      } finally { shaders.forEach((shader) => gl.deleteShader(shader)); }
    },
    render(gl) {
      const features = getFeatures();
      if (features !== previousFeatures) {
        rings = municipalityMaskRings(features);
        previousFeatures = features; cameraKey = "";
      }
      const w = gl.drawingBufferWidth, h = gl.drawingBufferHeight;
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, flat);
      if (w !== width || h !== height) {
        width = w; height = h; cameraKey = "";
        maskCanvas.width = w; maskCanvas.height = h;
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      }
      gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, w, h);
      const center = map.getCenter();
      const nextKey = `${center.lng}/${center.lat}/${map.getZoom()}/${map.getBearing()}/${w}/${h}`;
      if (nextKey !== cameraKey) {
        const canvas = map.getCanvas();
        maskContext.setTransform(w / canvas.clientWidth, 0, 0, h / canvas.clientHeight, 0, 0);
        maskContext.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
        maskContext.beginPath();
        for (const ring of rings) {
          ring.forEach((coordinate, i) => {
            const p = map.project(coordinate);
            if (i === 0) maskContext.moveTo(p.x, p.y);
            else maskContext.lineTo(p.x, p.y);
          });
          maskContext.closePath();
        }
        // ONE nonzero union coverage fill; no internal administrative strokes.
        maskContext.fillStyle = "#ffffff";
        maskContext.fill("nonzero");
        gl.bindTexture(gl.TEXTURE_2D, mask);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, maskCanvas);
        cameraKey = nextKey;
      }
    },
    onRemove(_map, gl) { dispose(gl); },
  };
  const restore: CustomLayerInterface = {
    id: TERRAIN_MASK_ID, type: "custom", renderingMode: "2d",
    render(gl) {
      gl.useProgram(program);
      gl.bindVertexArray(vao);
      gl.disable(gl.DEPTH_TEST); gl.disable(gl.STENCIL_TEST); gl.disable(gl.CULL_FACE);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, flat);
      gl.uniform1i(gl.getUniformLocation(program!, "flatBase"), 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, mask);
      gl.uniform1i(gl.getUniformLocation(program!, "coverage"), 1);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },
  };
  return [capture, restore];
}
